import { useCallback, useRef, useEffect, useState, useMemo } from 'react'
import { TourProvider, useTour, components as c, TourProps, StepType } from '@reactour/tour'
import styled, { css, createGlobalStyle } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import noop from 'lodash/noop'
import { colors, zIndex } from '@/styles'
import CloseButton from '@/components/close-button'
import useRole from '@/hooks/use-role'
import Tooltip from './tooltip.svg?react'
import useMaskSize, { Dimensions } from './use-mask-size'

type CssProps = Dimensions & {
  x: string
  y: string
}

const left = css<CssProps>`
  left: calc(var(--size) * -1);
  top: 50%;
  transform: translate(0px, -50%) rotate(90deg);
  ${props =>
    props?.height &&
    `
    transform: translate(0px, 0px) rotate(90deg);
    ${
      props.y === 'b'
        ? `
      top: unset;
      bottom: clamp(var(--inset), (var(--mask-height) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
        : `
      top: clamp(var(--inset), (var(--mask-height) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
    };
 `};
`

const right = css<CssProps>`
  right: calc(var(--size) * -1);
  top: 50%;
  transform: translate(0px, -50%) rotate(270deg);
  ${props =>
    props?.height &&
    `
    transform: translate(0px, 0px) rotate(270deg);
    ${
      props.y === 'b'
        ? `
      top: unset;
      bottom: clamp(var(--inset), (var(--mask-height) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
        : `
      top: clamp(var(--inset), (var(--mask-height) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
    };

 `};
`

const bottom = css<CssProps>`
  bottom: calc(var(--size) * -1);
  left: 50%;
  transform: translate(-50%, 0px) rotate(0deg);
  ${props =>
    props?.width &&
    `
    transform: translate(0px, 0px) rotate(0deg);
    ${
      props.x === 'r'
        ? `
      left: unset;
      right: clamp(var(--inset), (var(--mask-width) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
        : `
      left: clamp(var(--inset), (var(--mask-width) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
    };

 `};
`

const top = css<CssProps>`
  top: calc(var(--size) * -1);
  left: 50%;
  transform: translate(-50%, 0px) rotate(180deg);
  ${props =>
    props?.width &&
    `
    transform: translate(0px, 0px) rotate(180deg);
    ${
      props.x === 'r'
        ? `
      left: unset;
      right: clamp(var(--inset), (var(--mask-width) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
        : `
      left: clamp(var(--inset), (var(--mask-width) - var(--size)) / 2, calc(100% - var(--inset) - var(--size)));
    `
    };

 `};
`

const getOpposite = (position: PositionLocation): PointLocation => {
  if (position === 'left') return 'right'
  if (position === 'right') return 'left'
  if (position === 'top') return 'bottom'
  if (position === 'bottom') return 'top'
  return 'none'
}

const getTooltipStyle = (position: PointLocation) => {
  if (position === 'left') return left
  if (position === 'right') return right
  if (position === 'top') return top
  if (position === 'bottom') return bottom
  return ''
}

const TooltipWrapper = styled.div.withConfig<CssProps & PositionProps & Dimensions>({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    defaultValidatorFn(prop) && ['x', 'y', 'width', 'height'].indexOf(prop) < 0
})`
  --mask-width: ${props => props.width ?? '0'};
  --mask-height: ${props => props.height ?? '0'};
  --size: 20px;
  --inset: 4px;

  width: var(--size);
  height: var(--size);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: absolute;
  color: ${colors.white};
  text-align: center;
  ${({ position }) => getTooltipStyle(position)};
`

type BadgeProps = React.ComponentProps<typeof c.Badge>
type NavigationProps = React.ComponentProps<typeof c.Navigation>
type CloseProps = React.ComponentProps<typeof c.Close>

type AugmentedBadgeProps = BadgeProps &
  PositionProps & {
    positionStrategy: PositionStrategy
  }

const Navigation: React.FC<NavigationProps> = () => null

const BadgeStrategy: React.FC<AugmentedBadgeProps> = props => {
  const dimensions = useMaskSize()
  return <BadgeBase {...props} dimensions={dimensions} />
}

const defaultPosition = ['t', 'l']
const BadgeBase: React.FC<AugmentedBadgeProps & { dimensions?: Dimensions }> = ({
  position,
  positionStrategy,
  dimensions
}) => {
  const [x, y] = positionStrategy !== 'none' ? positionStrategy.split('') : defaultPosition
  return position !== 'none' ? (
    <TooltipWrapper aria-hidden position={position} {...dimensions} x={x} y={y}>
      <Tooltip />
    </TooltipWrapper>
  ) : null
}

const Close: React.FC<CloseProps> = ({ onClick, disabled, ...rest }) => {
  const { t } = useTranslation()
  return (
    <CloseButton
      onClick={onClick}
      disabled={disabled}
      top={12}
      right={12}
      iconSize={12}
      {...rest}
      aria-label={t('b.components.tour.close.label', 'Close')}
    />
  )
}
const defaultComponents = { Navigation, Close }

const padding = { mask: 12, popover: 14, wrapper: 0 }
const styles: TourProps['styles'] = {
  popover: base => ({
    ...base,
    zIndex: zIndex.modal + 1,
    background: colors.white,
    padding: '16px',
    borderRadius: '8px',
    opacity: 1,
    transition: 'none',
    boxShadow: 'none',
    animation: 'tour-opacity-popover 0.03s linear 0s 1'
  }),
  maskWrapper: base => ({
    ...base,
    zIndex: zIndex.modal,
    color: 'rgba(0, 0, 0, 1)',
    opacity: 0.6,
    transition: 'none',
    animation: 'tour-opacity-mask 0.03s linear 0s 1'
  }),
  maskArea: base => ({
    ...base,
    rx: 8
  })
}

type KeyboardPart = 'esc' | 'left' | 'right'
const disableKeyboardNavigation: KeyboardPart[] = ['right', 'left']

type PointLocation = 'none' | 'top' | 'left' | 'bottom' | 'right'
type PositionLocation = Exclude<PointLocation, 'none'> | 'center'
type PositionProps = { position: PointLocation }

const defaultProps = {
  defaultOpen: true,
  showCloseButton: true,
  disableFocusLock: false,
  disableKeyboardNavigation,
  className: 'tour-popover',
  maskClassName: 'tour-mask',
  padding,
  styles
}

const NoScroll = createGlobalStyle`
  html {
    overflow-y: hidden;
  }
  body {
    overflow: visible !important;
  }
`

const disableBody = (target: Target) => {
  if (!target) return
  return disableBodyScroll(target)
}
const enableBody = (target: Target) => {
  if (!target) return
  return enableBodyScroll(target)
}

const preventTouch = (e: Event) => {
  e.preventDefault()
}
const disableTouch = () => document.addEventListener('touchmove', preventTouch)
const enableTouch = () => document.removeEventListener('touchmove', preventTouch)

type Props = OnBeforeClose & { cancel: boolean }

// Workaround to tell if action was canceled onBeforeClose
const OnClose: React.FC<Props & { isOpen: boolean }> = ({ onBeforeClose, cancel, isOpen }) => {
  const called = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      called.current = true
      onBeforeClose(cancel)
    }
  }, [cancel, isOpen, onBeforeClose])

  // The useEffect below causes unspeakable pain by discretely hiding all highlights
  // when React.StrictMode is enabled. I've tested the onboarding flows and they all
  // seem to work without this, so not entirely sure why this even existed.
  // Leaving it as a comment for future reference and as a monument to my suffering:
  //
  // useEffect(() => {
  //   return () => {
  //     if (called?.current) return
  //     onBeforeClose(true)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  return null
}

const Inner: React.FC<Props> = ({ cancel, onBeforeClose }) => {
  const { isOpen } = useTour()
  return (
    <>
      {isOpen && <NoScroll />}
      <OnClose cancel={cancel} isOpen={!!isOpen} onBeforeClose={onBeforeClose} />
    </>
  )
}

type ContentProps = {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  transition?: boolean
  currentStep: number
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

type Step = Omit<StepType, 'content'> & {
  content:
    | React.ReactElement
    | string
    | ((props: ContentProps & { cancel: () => void; next: () => void }) => void)
}
export type StepProps = Step[]

type OnBeforeClose = {
  onBeforeClose: (canceled: boolean) => void
}

type PositionStrategyX = 'l' | 'r'
type PositionStrategyY = 't' | 'b'
type PositionStrategy = 'none' | `${PositionStrategyX}${PositionStrategyY}`

type Target = Element | null
type SimpleTourProps = Partial<OnBeforeClose> & {
  hide?: boolean
  onRequestClose?: () => void
  steps: StepProps
  point?: PointLocation
  onAfterOpen?: (target: Target) => void
  children?: React.ReactNode
  position?: PositionLocation
  positionStrategy?: PositionStrategy
  showCloseButton?: boolean
  allowInteraction?: boolean
}

const Tour: React.FC<SimpleTourProps> = ({
  hide = false,
  onRequestClose = noop,
  steps,
  children = null,
  onAfterOpen = noop,
  onBeforeClose = noop,
  point: initialPoint,
  position = 'top',
  positionStrategy = 'lt',
  allowInteraction = false,
  ...rest
}) => {
  const [cancel, setCancel] = useState(true)
  const [pageLoaded, setPageLoaded] = useState(false)
  const onClose = useCallback(onBeforeClose, [onBeforeClose])
  const step: StepType[] = useMemo(
    () =>
      steps.map(({ content, ...rest }) => ({
        content:
          typeof content === 'function'
            ? ({ setIsOpen, ...props }) => {
                const next = () => {
                  setCancel(false)
                  setIsOpen(false)
                }
                const cancel = () => {
                  setIsOpen(false)
                }
                return content({ next, cancel, setIsOpen, ...props })
              }
            : content,
        ...rest
      })),
    [steps]
  )

  useEffect(() => {
    const onPageLoad = () => {
      setPageLoaded(true)
    }
    if (document.readyState === 'complete') {
      onPageLoad()
    } else {
      window.addEventListener('load', onPageLoad, false)
      return () => window.removeEventListener('load', onPageLoad)
    }
  }, [])

  const point = initialPoint ? initialPoint : getOpposite(position)
  const components = useMemo(() => {
    const Badge = (props: BadgeProps) => (
      <BadgeStrategy {...props} position={point} positionStrategy={positionStrategy} />
    )
    const NoneBadge = (props: BadgeProps) => (
      <BadgeBase {...props} position={point} positionStrategy={positionStrategy} />
    )
    return {
      ...defaultComponents,
      Badge: positionStrategy !== 'none' ? Badge : NoneBadge
    }
  }, [point, positionStrategy])
  const onAfter = (target: Target) => {
    onAfterOpen(target)
    disableTouch()
    return disableBody(target)
  }
  const onBefore = (target: Target) => {
    enableTouch()
    return enableBody(target)
  }

  const role = useRole()
  const isLandlord = role === 'landlord'

  if (hide || !isLandlord || !steps || steps.length <= 0 || !pageLoaded) return <>{children}</>

  return (
    <TourProvider
      steps={step}
      afterOpen={onAfter}
      beforeClose={onBefore}
      components={components}
      position={position}
      onClickHighlighted={() => setCancel(false)}
      disableInteraction={!allowInteraction}
      {...defaultProps}
      {...rest}
    >
      <Inner cancel={cancel} onBeforeClose={onClose} />
      {children}
    </TourProvider>
  )
}

export default Tour
